import { useState } from "react";
import { PackageSearch, Truck } from "lucide-react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { ProductImagePlaceholder } from "@/components/site/Logo";
import { Reveal } from "@/components/site/Reveal";

const STATUS_LABELS = {
  paid: "Order confirmed",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  canceled: "Canceled",
  pending_payment: "Awaiting payment",
  expired: "Expired",
};

export default function OrderLookup() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.post("/orders/lookup", { order_number: orderNumber.trim(), email: email.trim() });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="pt-20" data-testid="order-lookup-page">
      <div className="bg-[#0A0A0A] gold-hairline-b py-16 sm:py-24 relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <Reveal>
            <p className="eyebrow mb-4">Your Order</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white font-light" data-testid="lookup-title">
              Track Your Order
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <form onSubmit={lookup} className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <p className="font-sans-body text-sm text-[#B8B8B8] font-light">
            Enter the order number from your confirmation email (e.g. KF-1A2B3C4D) and the
            email address used at checkout.
          </p>
          <div>
            <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Order Number</label>
            <input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="KF-XXXXXXXX"
              className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 font-mono-price text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] transition-colors"
              data-testid="lookup-order-number-input" />
          </div>
          <div>
            <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Email Address</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] transition-colors"
              data-testid="lookup-email-input" />
          </div>
          {error && <p className="font-sans-body text-xs text-red-400" data-testid="lookup-error">{error}</p>}
          <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="lookup-submit-button">
            {loading ? "Searching..." : "Find My Order"}
          </button>
        </form>

        {order && (
          <div className="gold-hairline bg-[#0D0D0D] p-8 mt-8 space-y-6" data-testid="lookup-result">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B]">Order</p>
                <p className="font-mono-price text-xl text-[#D4AF37]" data-testid="lookup-order-number">{order.order_number}</p>
              </div>
              <span className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] font-sans-body text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-2" data-testid="lookup-status">
                <PackageSearch size={14} /> {STATUS_LABELS[order.status] || order.status}
              </span>
            </div>

            <p className="font-sans-body text-xs text-[#666666]">
              Placed {new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>

            <div className="space-y-3 gold-hairline-t pt-5">
              {order.items.map((i) => (
                <div key={`${i.product_id}-${i.size}`} className="flex items-center gap-4">
                  <div className="w-10 h-14 shrink-0 overflow-hidden gold-hairline">
                    {i.image_url ? (
                      <img src={imageUrl(i.image_url)} alt={i.name} className="w-full h-full object-cover" />
                    ) : (
                      <ProductImagePlaceholder name={i.name} className="w-full h-full" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-sans-body text-sm text-white">{i.name}</p>
                    <p className="font-sans-body text-[10px] uppercase tracking-[0.15em] text-[#C6A15B]">{i.size} × {i.quantity}</p>
                  </div>
                  <span className="font-mono-price text-sm text-white">${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {order.tracking_number && (
              <div className="gold-hairline bg-[#080808] px-5 py-4 flex items-center gap-3" data-testid="lookup-tracking">
                <Truck size={16} className="text-[#D4AF37] shrink-0" />
                <div>
                  <p className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B]">Tracking Number</p>
                  <p className="font-mono-price text-sm text-white">{order.tracking_number}</p>
                </div>
              </div>
            )}

            <div className="flex justify-between gold-hairline-t pt-5">
              <span className="font-sans-body text-sm uppercase tracking-[0.2em] text-white">Total Paid</span>
              <span className="font-mono-price text-lg text-[#D4AF37]" data-testid="lookup-total">${order.total.toFixed(2)}</span>
            </div>

            <p className="font-sans-body text-[11px] text-[#666666] font-light">
              Questions about this order? <a href="/contact" className="text-[#D4AF37] underline underline-offset-2">Contact us</a> and mention your order number.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
